/**
 * Générateur — Automatisme : équivalences 1/10 = 0,1 ; 1/100 = 0,01 ; 1/1000 = 0,001
 * Attendu ID : 1006 | Niveau : 6ème | Domaine : NC
 * Rubrique : AUTOMATISME (programme 2025) — restitution en rituel court.
 * Labels (cartes exerciseur) :
 *   - Équivalences 1/10 = 0,1 ; 1/100 = 0,01 ; 1/1000 = 0,001
 *
 * ── Plan de cartes (même moule que gen_1005, 2026-08-20) ───────────────────
 * UNE carte, 5 formats rituels. Domaine modélisé par construction :
 * une équivalence est  n/10^p = écriture décimale de n·10^(-p)
 * avec p ∈ {1,2,3} et n ∈ [1..9] STRICTEMENT (arbitrage prof 2026-08-20 :
 * l'automatisme est restreint aux unitaires et à des extensions à UN chiffre
 * comme 5/100 = 0,05 — jamais un nombre à plusieurs chiffres, 25/1000 exclu,
 * quitte à avoir moins de variantes). 27 équivalences. INVARIANTS :
 *   - VALEUR TOUJOURS < 1 (n < 10^p) : les nombres ≥ 1 (56/10 = 5,6) et les
 *     sommes de fractions décimales relèvent de l'attendu SUIVANT — frontière
 *     A5 assumée, ne pas étendre ;
 *   - n jamais multiple de 10 : une seule écriture décimale canonique
 *     (0,30 vs 0,3 exclu par construction) ;
 *   - UN SEUL trou par égalité (dec = l'écriture décimale entière,
 *     num = le numérateur, den = le dénominateur) → réponse UNIQUE ;
 *   - chaque égalité émise est RE-VÉRIFIÉE numériquement (throw si fausse) ;
 *   - FOCUS UNITAIRE (arbitrage prof 2026-08-20) : le cœur de l'attendu est
 *     la restitution de 1/10 = 0,1, 1/100 = 0,01, 1/1000 = 0,001 — les
 *     extensions sont plafonnées à ~1/4 : saisie et V/F servent LES TROIS
 *     unitaires + 1 extension, le drag 2 unitaires + 1 extension, le QCM
 *     est unitaire 3 fois sur 4, le classer garantit le « 1 » ;
 *   - saisie/drag : au moins un trou « dec » ET un trou « num/den » par
 *     tirage — les DEUX sens de l'automatisme sont toujours testés ;
 *   - drag : mots tous DISTINCTS textuellement (aucune tuile juste ailleurs) ;
 *   - QCM : deux sous-types (contenu.sousType, canal persisté) —
 *     vers_decimale : échelle {n ; 0,n ; 0,0n ; 0,00n} (k = 0..3), la bonne
 *     est k = p ; vers_fraction : n/10, n/100, n/1000 + (10n)/10^p ;
 *   - V/F : les lignes fausses sont un décalage ×10 de l'écriture décimale,
 *     re-vérifiées FAUSSES numériquement ;
 *   - milliers : affichage 1\,000, mots de trous en brut « 1000 » ; décimales
 *     affichées 0{,}07 (KaTeX), mots de trous en brut « 0,07 ».
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, makeClasser, filtrerDistracteurs, tex,
} from '../../../utils';

const ATTENDU_ID = 1006;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Équivalences 1/10 = 0,1 ; 1/100 = 0,01 ; 1/1000 = 0,001';

// ─── Le domaine ────────────────────────────────────────────────────────────

/** Puissances de dix (index = exposant p). */
const P = [1, 10, 100, 1000];

/** Affichage d'une puissance : espace fine des milliers, jamais de virgule. */
const fmtTex = (n) => (n === 1000 ? '1\\,000' : String(n));

/**
 * Écriture décimale BRUTE de n·10^(-k) : `decStr(7, 2)` → « 0,07 »,
 * `decStr(25, 3)` → « 0,025 », `decStr(7, 0)` → « 7 ».
 * n jamais multiple de 10 → aucune écriture à zéro final possible.
 */
const decStr = (n, k) => {
    const s = String(n);
    if (k === 0) return s;
    if (s.length <= k) return `0,${'0'.repeat(k - s.length)}${s}`;
    return `${s.slice(0, -k)},${s.slice(-k)}`;
};

/** Même écriture pour KaTeX : virgule française `{,}` (sans espace parasite). */
const decTex = (n, k) => decStr(n, k).replace(',', '{,}');

/**
 * Numérateurs licites : UN SEUL CHIFFRE (arbitrage prof 2026-08-20).
 * L'automatisme est restreint à 1/10, 1/100, 1/1000 ; les extensions
 * n'existent que sous la forme n/10^p avec n ∈ [2..9] (5/100 = 0,05),
 * jamais un nombre (25/1000 exclu — quitte à avoir moins de variantes).
 */
const numerateurs = () => [1, 2, 3, 4, 5, 6, 7, 8, 9];

/** Tire un item { n, p, trou, sens } ; trou ∈ {'dec','num','den'}. */
const tirerUnItem = (trous = ['dec', 'num', 'den']) => {
    const p = randInt(1, 3);
    return {
        n: randomChoice(numerateurs()),
        p,
        trou: randomChoice(trous),
        sens: randInt(0, 1),
    };
};

/** Clé de l'équivalence sous-jacente (sans trou ni sens). */
const cleEquiv = (item) => `${item.n}|${item.p}`;

/** Valeur attendue dans le trou (mot BRUT que l'élève tape ou glisse). */
const reponseDe = (item) => (
    item.trou === 'dec' ? decStr(item.n, item.p)
        : item.trou === 'num' ? String(item.n)
            : String(P[item.p])
);

/**
 * L'égalité en LaTeX (sans les $). `slot` : null (ligne pleine),
 * 'trou' ({{réponse}} — triple accolade automatique dans \dfrac), 'question' (?).
 * Membre « donnée » = l'écriture décimale si le trou est dans la fraction,
 * la fraction sinon ; `sens` échange l'ordre d'affichage.
 */
const texEgalite = (item, slot = null) => {
    const marque = slot === 'trou' ? `{{${reponseDe(item)}}}` : slot === 'question' ? '?' : null;
    const num = item.trou === 'num' && marque !== null ? marque : String(item.n);
    const den = item.trou === 'den' && marque !== null ? marque : fmtTex(P[item.p]);
    const frac = `\\dfrac{${num}}{${den}}`;
    const dec = item.trou === 'dec' && marque !== null ? marque : decTex(item.n, item.p);
    const [g, d] = item.trou === 'dec' ? [frac, dec] : [dec, frac];
    return item.sens === 0 ? `${g} = ${d}` : `${d} = ${g}`;
};

/**
 * AUTO-VÉRIFICATION : n/10^p et l'écriture décimale émise désignent le même
 * nombre — throw sinon (on préfère refuser d'émettre qu'émettre faux).
 */
const verifierItem = (item) => {
    const attendu = item.n / P[item.p];
    const affiche = Number(decStr(item.n, item.p).replace(',', '.'));
    if (item.n >= P[item.p] || item.n % 10 === 0
        || Math.abs(attendu - affiche) > 1e-12) {
        throw new Error(`gen_1006 : équivalence fausse par construction (${cleEquiv(item)})`);
    }
    return item;
};

/**
 * Tire `nb` items d'équivalences DISTINCTES (deux trous d'une même
 * équivalence se dévoileraient l'un l'autre), avec :
 *   - `unitaires` équivalences UNITAIRES 1/10^p (rangs distincts) : le CŒUR
 *     de l'attendu (« 1/10 = 0,1 ; 1/100 = 0,01 ; 1/1000 = 0,001 »), garanti
 *     dans chaque tirage — les n/10^p (un chiffre) ne sont que l'extension,
 *     plafonnée à ~1/4 du tirage (focus arbitré par le prof, 2026-08-20) ;
 *   - au moins un trou 'dec' ET un trou 'num'/'den' (les deux sens) ;
 *   - mots de trous tous DISTINCTS si `motsDistincts` (drag).
 * Boucle bornée ; repli déterministe conforme aux contraintes.
 */
const tirerItems = (nb, { motsDistincts = false, unitaires = 3 } = {}) => {
    for (let essai = 0; essai < 80; essai++) {
        const pris = [];
        const cles = new Set();
        const mots = new Set();
        const ajouter = (it) => {
            if (cles.has(cleEquiv(it))) return;
            if (motsDistincts && mots.has(reponseDe(it))) return;
            cles.add(cleEquiv(it));
            mots.add(reponseDe(it));
            pris.push(verifierItem(it));
        };
        // 1) le cœur : les équivalences unitaires, à rangs distincts.
        for (const p of shuffle([1, 2, 3]).slice(0, unitaires)) {
            ajouter({ n: 1, p, trou: randomChoice(['dec', 'num', 'den']), sens: randInt(0, 1) });
        }
        // 2) l'extension : des n/10^p (n ≥ 2) pour compléter.
        for (let t = 0; t < 200 && pris.length < nb; t++) {
            const it = tirerUnItem();
            if (it.n === 1) continue;
            ajouter(it);
        }
        if (pris.length === nb
            && pris.filter((it) => it.n === 1).length >= Math.min(unitaires, nb)
            && pris.some((it) => it.trou === 'dec')
            && pris.some((it) => it.trou !== 'dec')) return shuffle(pris);
    }
    // Repli déterministe : les 3 unitaires + 1 extension, mots distincts.
    return [
        { n: 1, p: 1, trou: 'dec', sens: 0 },   // 1/10 = {{0,1}}
        { n: 1, p: 2, trou: 'den', sens: 0 },   // 0,01 = 1/{{100}}
        { n: 1, p: 3, trou: 'dec', sens: 1 },   // {{0,001}} = 1/1000
        { n: 7, p: 2, trou: 'num', sens: 0 },   // 0,07 = {{7}}/100
    ].slice(0, nb).map(verifierItem);
};

// ─── Format 1 : texte à trous SAISIE (la série flash) ─────────────────────

const genEquivalencesTrouSaisie = () => {
    const items = tirerItems(4);
    const phrase = items.map((it) => `$${texEgalite(it, 'trou')}$`).join('\n');
    return makeTexteTrou(
        phrase,
        '<p><b>Complète chaque égalité.</b></p>',
        { ...opts(L1), mode: 'saisie' },
    );
};

// ─── Format 2 : texte à trous DRAG ────────────────────────────────────────

const genEquivalencesTrouDrag = () => {
    const items = tirerItems(3, { motsDistincts: true, unitaires: 2 });
    const phrase = items.map((it) => `$${texEgalite(it, 'trou')}$`).join('\n');
    // UN piège PAR LIGNE (arbitrage prof 2026-08-20 : aucune ligne sans
    // voisin piégeux) : chaque égalité choisit un distracteur dans SES
    // candidats (décalage ×10 du décimal, puissance voisine, zéro ajouté),
    // filtré contre les mots et dédoublonné numériquement.
    const candidatsDe = (it) => {
        if (it.trou === 'dec') {
            const c = [decStr(it.n, it.p + 1)];
            if (it.p > 1 || it.n < 10) c.push(decStr(it.n, it.p - 1));
            return c;
        }
        if (it.trou === 'den') {
            return [String(P[it.p === 3 ? 2 : it.p + 1]), String(P[it.p === 1 ? 3 : it.p - 1])];
        }
        return [String(it.n * 10)];
    };
    const distracteurs = [];
    const vusVal = new Set();
    for (const it of items) {
        const ok = filtrerDistracteurs(phrase, shuffle(candidatsDe(it)))
            .find((d) => !vusVal.has(Number(d.replace(',', '.'))));
        if (ok) {
            vusVal.add(Number(ok.replace(',', '.')));
            distracteurs.push(ok);
        }
    }
    return makeTexteTrou(
        phrase,
        '<p><b>Complète chaque égalité avec les bonnes étiquettes.</b></p>',
        { ...opts(L1), mode: 'drag', distracteurs },
    );
};

// ─── Format 3 : QCM (deux sous-types, cf. REGLES_DISTRACTEURS_QCM) ────────

const genEquivalencesQcm = () => {
    const sousType = randomChoice(['vers_decimale', 'vers_fraction']);
    // 3 tirages sur 4 portent sur une équivalence UNITAIRE 1/10^p, 1 sur 4
    // sur une extension n ∈ [2..9] (arbitrage prof 2026-08-20 : l'automatisme
    // EST la restitution des trois faits, quitte à revoir souvent les mêmes
    // questions — le doublon successif, ~7 %, est assumé).
    const p = randInt(1, 3);
    const n = randInt(1, 4) === 1 ? randInt(2, 9) : 1;
    const it = verifierItem({ n, p, trou: 'dec', sens: 0 });
    let payload;
    if (sousType === 'vers_decimale') {
        // Échelle k = 0..3 : la bonne est k = p, chaque autre est une erreur
        // nommée (k = 0 : dénominateur ignoré ; k < p : un zéro de moins que
        // nécessaire, nombre trop grand ; k > p : un zéro de trop).
        const choix = [0, 1, 2, 3].map((k) => decStr(it.n, k));
        const texte = '<p><b>Quelle est l\'écriture décimale de '
            + `${tex(`\\dfrac{${it.n}}{${fmtTex(P[it.p])}}`)} ?</b></p>`;
        payload = makeQcm(choix, decStr(it.n, it.p), texte, opts(L1), false);
        payload._meta.params = {
            ...it,
            sousType,
            distracteurs: [0, 1, 2, 3].filter((k) => k !== it.p).map((k) => ({
                valeur: decStr(it.n, k),
                code: k === 0 ? 'denominateur_ignore'
                    : (k < it.p ? 'decalage_x10_haut' : 'decalage_x10_bas'),
            })),
        };
    } else {
        // n/10, n/100, n/1000 (la bonne : dénominateur 10^p) + (10n)/10^p
        // (zéro ajouté au numérateur — textuellement distinct par nature).
        const fracStr = (num, pw) => `$\\dfrac{${num}}{${fmtTex(P[pw])}}$`;
        const bonne = fracStr(it.n, it.p);
        const choix = [fracStr(it.n, 1), fracStr(it.n, 2), fracStr(it.n, 3),
            fracStr(it.n * 10, it.p)];
        const texte = '<p><b>Quelle fraction décimale est égale à '
            + `${tex(decTex(it.n, it.p))} ?</b></p>`;
        payload = makeQcm(choix, bonne, texte, opts(L1), false);
        payload._meta.params = {
            ...it,
            sousType,
            distracteurs: [
                ...[1, 2, 3].filter((pw) => pw !== it.p).map((pw) => ({
                    valeur: fracStr(it.n, pw),
                    code: pw < it.p ? 'decalage_x10_haut' : 'decalage_x10_bas',
                })),
                { valeur: fracStr(it.n * 10, it.p), code: 'zero_ajoute_numerateur' },
            ],
        };
    }
    payload.contenu = { ...payload.contenu, sousType };
    return payload;
};

// ─── Format 4 : tableau Vrai/Faux ─────────────────────────────────────────

/**
 * Ligne fausse = l'écriture décimale décalée d'un facteur 10 (l'erreur cible
 * de l'attendu), p' = p ± 1 borné à [0..4] ; re-vérifiée FAUSSE numériquement.
 */
const fausserItem = (item) => {
    const pFaux = item.p === 1 ? 2 : item.p === 3 ? 2 : randomChoice([1, 3]);
    const frac = `\\dfrac{${item.n}}{${fmtTex(P[item.p])}}`;
    const dec = decTex(item.n, pFaux);
    const affiche = Number(decStr(item.n, pFaux).replace(',', '.'));
    if (Math.abs(item.n / P[item.p] - affiche) < 1e-12) {
        throw new Error('gen_1006 : la ligne censée être fausse est vraie');
    }
    return item.sens === 0 ? `${frac} = ${dec}` : `${dec} = ${frac}`;
};

const genEquivalencesVraiFaux = () => {
    let lignes = [];
    let etiquettes = [];
    for (let essai = 0; essai < 30; essai++) {
        const items = tirerItems(4);
        const nbVraies = randInt(1, 3);
        const etiq = shuffle([
            ...Array(nbVraies).fill(0),
            ...Array(4 - nbVraies).fill(1),
        ]);
        const ls = items.map((it, i) => (
            etiq[i] === 0 ? `$${texEgalite(it)}$` : `$${fausserItem(it)}$`
        ));
        lignes = ls;
        etiquettes = etiq;
        if (new Set(ls).size === 4) break;
    }
    return makeVraiFaux(
        lignes,
        etiquettes,
        '<p><b>Vrai ou faux ?</b></p>',
        opts(L1),
    );
};

// ─── Format 5 : classer ───────────────────────────────────────────────────

/**
 * Catégories = le RANG du chiffre non nul (n ∈ [1..9] seulement : un unique
 * chiffre non nul → catégorie UNIQUE par construction, pas d'écriture à
 * cheval comme 0,25). Items : fractions ET écritures décimales mêlées.
 */
const genEquivalencesClasser = () => {
    const noms = ['Des dixièmes', 'Des centièmes', 'Des millièmes'];
    // Le « 1 » (équivalence unitaire, cœur de l'attendu) est garanti dans une
    // des trois catégories à chaque tirage (arbitrage prof 2026-08-20).
    const catUnitaire = randInt(0, 2);
    const categories = noms.map((name, idx) => {
        const p = idx + 1;
        const ns = idx === catUnitaire
            ? shuffle([1, ...shuffle([2, 3, 4, 5, 6, 7, 8, 9]).slice(0, randInt(1, 2))])
            : shuffle([2, 3, 4, 5, 6, 7, 8, 9]).slice(0, randInt(2, 3));
        const items = ns.map((n, i) => {
            verifierItem({ n, p, trou: 'dec', sens: 0 });
            // Alternance fraction / décimal pour mêler les deux écritures.
            return i % 2 === 0 ? `$\\dfrac{${n}}{${fmtTex(P[p])}}$` : `$${decTex(n, p)}$`;
        });
        return { name, items };
    });
    return makeClasser(
        categories,
        '<p><b>Classe chaque écriture : dixièmes, centièmes ou millièmes ?</b></p>',
        opts(L1),
    );
};

// ─── Contrat distracteurs (référence de revue, format QCM) ────────────────

/**
 * Chaque erreur est une MISCONCEPTION NOMMÉE d'élève, jamais du bruit.
 */
export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'les choix sont TOUJOURS des échelles complètes (jamais de bouche-trou) : vers_decimale = {n ; 0,n ; 0,0n ; 0,00n}, vers_fraction = {n/10 ; n/100 ; n/1000 ; 10n/10^p}',
        'distinction TEXTUELLE garantie par construction (nombres de décimales ou dénominateurs tous différents ; 10n ≠ n)',
        'chaque distracteur porte son code dans _meta.params.distracteurs (futur feedback pédagogique nommant l\'erreur)',
    ],
    equivalences_qcm_unique: {
        support: 'une équivalence n/10^p ↔ écriture décimale (valeur < 1), question dans un sens ou dans l\'autre (contenu.sousType)',
        fixes: {
            denominateur_ignore: 'le choix « n » tel quel : l\'élève ignore le dénominateur (croit que 7/100 = 7)',
            decalage_x10_haut: 'un zéro de moins que nécessaire : nombre 10 fois trop grand (7/100 = 0,7)',
            decalage_x10_bas: 'un zéro de trop : nombre 10 fois trop petit (7/100 = 0,007)',
            zero_ajoute_numerateur: 'vers_fraction : le numérateur reçoit un zéro (0,07 = 70/100) — confusion « plus de chiffres = même nombre »',
        },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    equivalences_texte_trou_saisie: genEquivalencesTrouSaisie,
    equivalences_texte_trou_drag: genEquivalencesTrouDrag,
    equivalences_qcm_unique: genEquivalencesQcm,
    equivalences_tableau_vf: genEquivalencesVraiFaux,
    equivalences_classer: genEquivalencesClasser,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    tableau_vf: 'Vrai/Faux', classer: 'Classer',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'equivalences', label: L1, formatsList: buildFormats('equivalences') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
